import { FileText, ShieldCheck, AlertOctagon, Scale, Mail } from "lucide-react";

export default function Terms() {
    return (
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="terms-page">
            <div className="mb-10">
                <div className="overline mb-3">Legal</div>
                <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
                    Terms of Service<span className="text-accent">.</span>
                </h1>
                <p className="text-zinc-500">Last updated: February 2026</p>
            </div>

            <div className="surface p-8 mb-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-md border border-accent/30 bg-accent/10 flex-shrink-0 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-accent" strokeWidth={1.75} />
                </div>
                <div>
                    <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-1">
                        Agreement between you and Ari Enterprises
                    </h2>
                    <p className="text-sm text-zinc-400 leading-relaxed">
                        These Terms of Service (&quot;Terms&quot;) govern your access to and use of
                        listcraft (the &quot;Service&quot;), operated by <span className="text-zinc-200">Ari Enterprises</span>. By
                        creating an account or purchasing credits, you agree to be bound by these Terms.
                    </p>
                </div>
            </div>

            <div className="space-y-8 text-zinc-300 leading-relaxed">
                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">1. The Service</h3>
                    <p className="text-sm text-zinc-400">
                        listcraft is a software-as-a-service tool that generates SEO-optimized product
                        titles, descriptions, bullet points, meta tags and variant copy for online
                        marketplaces (Amazon, Shopify, Etsy, eBay and similar). Outputs are produced by
                        third-party AI models and are made available to you inside your account
                        dashboard.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">2. Eligibility &amp; account</h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>You must be at least 18 years old and legally able to enter into a contract.</li>
                        <li>You are responsible for keeping your login credentials confidential and for all activity that occurs on your account.</li>
                        <li>You must provide accurate information at signup and update it if it changes.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">3. Credits &amp; payments</h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>listcraft is priced on a credit-pack basis. There are no recurring subscriptions.</li>
                        <li>Payments are processed by Razorpay. Once payment is captured, credits are added to your account.</li>
                        <li>Credits are non-transferable and non-exchangeable for cash.</li>
                        <li>Refunds are handled per our <a href="/refund-policy" className="text-accent hover:underline">Cancellation &amp; Refund Policy</a>.</li>
                        <li>Applicable taxes (including GST for Indian customers) are shown on invoices available under <span className="text-zinc-200">Billing</span>.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-accent" /> 4. Your content &amp; ownership
                    </h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>You retain full ownership of the product photos, specifications and any content you upload.</li>
                        <li>By uploading, you grant Ari Enterprises a limited, non-exclusive license to process that content solely to deliver the Service (e.g., sending it to the AI model to generate copy).</li>
                        <li>Subject to your payment for the credit consumed, you own the AI-generated output and may use it commercially on your marketplace listings.</li>
                        <li>You are responsible for ensuring you have the right to upload the images you provide and that the resulting listings comply with the target marketplace&apos;s policies.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <AlertOctagon className="w-4 h-4 text-accent" /> 5. Acceptable use
                    </h3>
                    <p className="text-sm text-zinc-400 mb-2">You agree not to use the Service to:</p>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>Generate listings for illegal, counterfeit, hateful, sexually explicit involving minors, or otherwise unlawful goods.</li>
                        <li>Reverse-engineer, scrape, or attempt to bypass our billing or credit system.</li>
                        <li>Resell or white-label the raw Service without a written agreement.</li>
                        <li>Upload content that infringes intellectual property rights of third parties.</li>
                    </ul>
                    <p className="text-sm text-zinc-400 mt-2">
                        We may suspend or terminate accounts that violate this section, with or without
                        notice. In serious cases, unused credits may be forfeited.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">6. AI-generated content disclaimer</h3>
                    <p className="text-sm text-zinc-400">
                        AI outputs can occasionally be inaccurate, biased, or non-compliant with a
                        specific marketplace&apos;s current policies. You are responsible for reviewing
                        every listing before publishing it. Ari Enterprises does not guarantee ranking,
                        sales performance, or approval by any marketplace.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">7. Availability &amp; changes</h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>We aim for high availability but do not guarantee uninterrupted service.</li>
                        <li>We may add, modify, or remove features. Material changes will be communicated in-app or via email.</li>
                        <li>Pricing may change with prior notice, but existing purchased credits will honour the price at time of purchase.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <Scale className="w-4 h-4 text-accent" /> 8. Liability
                    </h3>
                    <p className="text-sm text-zinc-400">
                        To the maximum extent permitted by law, Ari Enterprises&apos; total liability
                        arising out of or related to the Service will not exceed the total amount you
                        paid to us in the three (3) months preceding the claim. We are not liable for
                        indirect, incidental, or consequential damages such as lost sales or lost data.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">9. Termination</h3>
                    <p className="text-sm text-zinc-400">
                        You may stop using the Service at any time. We may suspend or terminate your
                        access for breach of these Terms or for fraudulent activity. Termination does
                        not entitle you to a refund except as allowed by our Cancellation &amp; Refund
                        Policy.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">10. Governing law</h3>
                    <p className="text-sm text-zinc-400">
                        These Terms are governed by the laws of India. Any disputes arising will be
                        subject to the exclusive jurisdiction of the courts where Ari Enterprises is
                        registered.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-accent" /> 11. Contact
                    </h3>
                    <p className="text-sm text-zinc-400">
                        Ari Enterprises · <a href="mailto:arienterprises50@gmail.com" className="text-accent hover:underline">arienterprises50@gmail.com</a>
                    </p>
                </section>
            </div>
        </div>
    );
}
